import * as THREE from 'three';
import type { BattleState, MapDef } from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import type { OmenCtx } from '../omens/common.ts';
import { WORLD_PER_HEX } from './coords.ts';
import { GlowLayer } from './glow.ts';
import type { TerrainBuild } from './terrain.ts';

/**
 * The low water (La Orilla 1, docs/SAGAS.md): the stilt-houses on the titan's fore-fin, and the reef out where the sea
 * went. The node is the fishers' stilt-houses: huts on poles round a boardwalk, nets drying, boats pulled up. Out along
 * `rules.tide.dir` (the longest road, the causeway) the ground is sea floor laid bare: wet sand, pools, weed, stranded
 * fish, the causeway's stepping stones, and at its far end the reef where the great net is snagged.
 *
 * The water (`s.tide.front`): one wide sheet of sea whose shader keeps only what lies past the front along the way
 * out (`dot(pos, dir)/1000 >= front`, as sim/battle/tide.ts `tideWet`), with a band of white foam at the front. Before
 * it turns it waits past the reef; then it comes back over the map. Once it has turned the light goes to a moonlit
 * night, and at dawn to a warm sunrise.
 *
 * The net (`s.tide.net`): snagged on the reef (a ring of cold dots fills as heroes work it loose), a heap with a cold
 * ring on the ground, a bundle over its carrier's shoulders, hung on the stilt-houses' frame once home. A Drowned with
 * a hero in its grip (`e.grab`) trails a rope to them.
 *
 * Draw calls: the shore and the reef (one merged mesh), the lamps (one), the sea (one), the net snagged, hung and
 * bundled (three, one shown at a time but the hung one), the ropes (one instanced), a glow layer. No lights.
 */

type Rules = NonNullable<NonNullable<BattleState['rules']>['tide']>;

const K = WORLD_PER_HEX / 1000;
const COOL = new THREE.Color('#cfe8ff');
const LAMP = new THREE.Color('#ffd6a0');
const FOAM = new THREE.Color('#ffffff');
const WET = new THREE.Color('#6a6250');
const WET_FAR = new THREE.Color('#4a4a44');
const SAND = new THREE.Color('#9a8c6c');
const TIMBER = '#5e564a';
const TIMBER_D = '#3a342e';
const PLANK = '#6c665a';
const THATCH = '#7a6c4c';
const CORD = '#5a6252';
const CORK = '#b08a5a';
const ROCK = ['#3a3c40', '#46484c', '#2e3034'];
const CORAL = ['#b05a46', '#c8a088', '#7a4a6a', '#a07050'];
const MAX_ROPES = 12;

/** The sea: past the front along the way out, a foam band at its edge, a slow swell. */
export const seaMat = (dir: { x: number; y: number }): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    uniforms: {
      front: { value: 1e9 },
      time: { value: 0 },
      foam: { value: 650 },
      dir: { value: new THREE.Vector2(dir.x / 1000, dir.y / 1000) },
      k: { value: K },
      night: { value: 0 },
    },
    vertexShader: /* glsl */ `
      varying vec2 vXZ;
      uniform float time;
      void main() {
        vXZ = position.xz;
        vec3 p = position;
        p.y += 0.035 * sin(p.x * 1.7 + time * 1.6) * cos(p.z * 1.3 - time * 1.2);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
      }`,
    fragmentShader: /* glsl */ `
      varying vec2 vXZ;
      uniform float front;
      uniform float time;
      uniform float foam;
      uniform vec2 dir;
      uniform float k;
      uniform float night;
      void main() {
        float along = (vXZ.x * dir.x + vXZ.y * dir.y) / k;
        if (along < front) discard;
        float into = along - front;
        float edge = 1.0 - smoothstep(0.0, foam, into);
        float lace = 0.5 + 0.5 * sin(vXZ.x * 9.0 + vXZ.y * 7.0 + time * 4.0) * sin(vXZ.x * 5.0 - vXZ.y * 6.0 - time * 3.0);
        float rip = 0.5 + 0.5 * sin(vXZ.x * 2.4 + vXZ.y * 2.1 + time * 1.8);
        vec3 shallow = vec3(0.18, 0.40, 0.44);
        vec3 deep = vec3(0.07, 0.18, 0.26);
        vec3 col = mix(shallow, deep, smoothstep(0.0, 6000.0, into));
        col += 0.05 * rip;
        col = mix(col, vec3(0.95, 0.98, 1.0), edge * (0.7 + 0.3 * lace));
        col *= mix(1.0, 0.55, night);
        float a = mix(0.82, 0.96, edge);
        gl_FragColor = vec4(col, a);
      }`,
  });

export class ShoreLayer {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private glow = new GlowLayer(200);
  private sea: THREE.Mesh;
  private seaM: THREE.ShaderMaterial;
  private reefNet: THREE.Mesh;
  private homeNet: THREE.Mesh;
  private bundle: THREE.Mesh;
  private ropes: THREE.InstancedMesh;
  /** The reef (world), the net's place on the stilt-houses' frame (world), the lamps (world). */
  private reef: THREE.Vector3;
  private lamps: THREE.Vector3[] = [];
  private dir: { x: number; y: number };
  private now = 0;
  private sky: Sky;
  private v = new THREE.Vector3();
  private v2 = new THREE.Vector3();
  private m = new THREE.Matrix4();
  private q = new THREE.Quaternion();
  private s3 = new THREE.Vector3();
  private c = new THREE.Color();

  constructor(
    private ctx: OmenCtx,
    private map: MapDef,
    private r: Rules,
  ) {
    this.group.name = 'shore';
    this.dir = r.dir;
    const box = new THREE.BoxGeometry(1, 1, 1);
    const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 8);
    const rock = new THREE.DodecahedronGeometry(0.5, 0);
    const disc = new THREE.CircleGeometry(0.5, 14).rotateX(-Math.PI / 2);
    const cone = new THREE.ConeGeometry(0.5, 1, 5);
    const ball = new THREE.SphereGeometry(0.5, 8, 6);
    const kit = new Kit();
    const lit = new Kit();
    const rng = mulberry(r.reef.x * 7 + r.reef.y * 13);
    const R = (): number => rng() * 2 - 1;
    const out = (x: number, y: number): number => (x * this.dir.x + y * this.dir.y) / 1000;
    // Sideways to the way out, in world (x, z).
    const sideX = -this.dir.y / 1000;
    const sideZ = this.dir.x / 1000;
    const outX = this.dir.x / 1000;
    const outZ = this.dir.y / 1000;

    // ---- The stilt-houses on the node, facing the sea.
    const hang = stiltHouses(ctx, kit, lit, outX, outZ, R, this.lamps, { box, cyl, ball });

    // ---- The causeway: the road out to the reef, its stones laid bare.
    const road = map.sectors.find((sec) => {
      const f = sec.path[0];
      if (!f) return false;
      const w = this.ctx.at(r.reef.x, r.reef.y, 0);
      const c = cellWorld(f.q, f.r);
      return Math.abs(c.x - w.x) < 0.05 && Math.abs(c.z - w.z) < 0.05;
    });
    for (const h of road?.path ?? []) {
      const c = cellWorld(h.q, h.r);
      const sx = c.x / K;
      const sy = c.z / K;
      if (out(sx, sy) < r.shore - 600) continue;
      for (let k = 0; k < 3; k++) {
        const x = c.x + R() * 0.35;
        const z = c.z + R() * 0.35;
        kit.add(rock, ROCK[k % 3] as string, place(x, ctx.ground(x, z) + 0.03, z, R() * 3, new THREE.Vector3(0.42 + 0.15 * R(), 0.12, 0.36 + 0.12 * R())));
      }
      kit.add(cone, '#2f4a2a', place(c.x + R() * 0.4, ctx.ground(c.x, c.z) + 0.06, c.z + R() * 0.4, R() * 3, new THREE.Vector3(0.12, 0.16, 0.05), 0.4 * R()));
    }

    // ---- The sea floor laid bare: pools, weed, shells, a stranded fish or two, out past the shore line.
    const span = (map.radius + 1) * WORLD_PER_HEX;
    let placed = 0;
    for (let i = 0; i < 260 && placed < 90; i++) {
      const x = R() * span;
      const z = R() * span;
      if (Math.hypot(x, z) > span) continue;
      const o = out(x / K, z / K);
      if (o < r.shore + 300) continue;
      const gy = ctx.ground(x, z);
      const kind = i % 6;
      placed++;
      if (kind === 0) kit.add(disc, '#2a4048', place(x, gy + 0.015, z, R() * 3, new THREE.Vector3(0.7 + 0.5 * rng(), 1, 0.5 + 0.4 * rng())));
      else if (kind === 1 || kind === 4) for (let w = 0; w < 3; w++) kit.add(cone, w % 2 ? '#2f4a2a' : '#3e5a2e', place(x + R() * 0.15, gy + 0.07, z + R() * 0.15, R() * 3, new THREE.Vector3(0.05, 0.16 + 0.08 * rng(), 0.03), 0.6 * R(), 0.6 * R()));
      else if (kind === 2) kit.add(rock, ROCK[i % 3] as string, place(x, gy + 0.06, z, R() * 3, 0.18 + 0.2 * rng()));
      else if (kind === 3) kit.add(ball, '#d8cfc0', place(x, gy + 0.02, z, R() * 3, new THREE.Vector3(0.07, 0.03, 0.06)));
      else {
        // A stranded fish: a silver body and a tail.
        const f = place(x, gy + 0.04, z, R() * 3);
        kit.add(ball, '#9aa6b0', f, place(0, 0, 0, 0, new THREE.Vector3(0.24, 0.06, 0.09)));
        kit.add(cone, '#7a8690', f, place(-0.15, 0, 0, 0, new THREE.Vector3(0.08, 0.1, 0.02), 0, Math.PI / 2));
      }
    }

    // ---- The reef at the causeway's end: black rock crusted with coral, shells, weed.
    const reef = ctx.at(r.reef.x, r.reef.y, 0);
    this.reef = reef;
    const rg = ctx.ground(reef.x, reef.z);
    for (let k = 0; k < 9; k++) {
      const a = (k / 9) * Math.PI * 2 + 0.3 * R();
      const d = 0.5 + 0.45 * rng();
      const x = reef.x + Math.cos(a) * d;
      const z = reef.z + Math.sin(a) * d;
      kit.add(rock, ROCK[k % 3] as string, place(x, rg + 0.18, z, R() * 3, new THREE.Vector3(0.55 + 0.3 * rng(), 0.45 + 0.3 * rng(), 0.5 + 0.3 * rng())));
      // Coral: little branching fans and knobs on the rocks.
      for (let b = 0; b < 3; b++) kit.add(cone, CORAL[(k + b) % CORAL.length] as string, place(x + R() * 0.2, rg + 0.4 + 0.1 * rng(), z + R() * 0.2, R() * 3, new THREE.Vector3(0.05, 0.22 + 0.1 * rng(), 0.05), 0.5 * R(), 0.5 * R()));
      kit.add(ball, CORAL[k % CORAL.length] as string, place(x + R() * 0.2, rg + 0.36, z + R() * 0.2, 0, 0.1));
    }
    // A broken spar and a float, the net's wreckage round it.
    kit.add(cyl, TIMBER_D, place(reef.x + sideX * 0.6, rg + 0.15, reef.z + sideZ * 0.6, 0.7, new THREE.Vector3(0.05, 1.1, 0.05), 0.2, 1.3));

    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.82, metalness: 0.02, flatShading: true });
    const litMat = new THREE.MeshBasicMaterial({ vertexColors: true, toneMapped: false });
    this.mats.push(mat, litMat);
    for (const [k, m, name] of [
      [kit, mat, 'shore-land'],
      [lit, litMat, 'shore-lamps'],
    ] as const) {
      const mesh = k.build(m, name);
      if (!mesh) continue;
      mesh.receiveShadow = m === mat;
      mesh.castShadow = m === mat && !ctx.low;
      this.geos.push(mesh.geometry);
      this.group.add(mesh);
    }

    // ---- The net, three ways: snagged on the reef, hung on the frame at home, bundled (on the ground or carried).
    const netMat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.95, side: THREE.DoubleSide });
    this.mats.push(netMat);
    const mesh = (kt: Kit, name: string): THREE.Mesh => {
      const m = kt.build(netMat, name) as THREE.Mesh;
      this.geos.push(m.geometry);
      this.group.add(m);
      return m;
    };
    const drape = (kt: Kit, w: number, h: number, sag: number, m: THREE.Matrix4): void => {
      const COLS = 11;
      const ROWS = 7;
      const pt = (i: number, j: number): THREE.Vector3 => {
        const u = i / (COLS - 1);
        const v = j / (ROWS - 1);
        return new THREE.Vector3((u * 2 - 1) * w, -v * h - Math.sin(Math.PI * u) * sag * v, 0.05 * Math.sin(u * 7 + v * 4)).applyMatrix4(m);
      };
      const seg = (a: THREE.Vector3, b: THREE.Vector3): void => {
        const d = new THREE.Vector3().subVectors(b, a);
        const len = d.length();
        const g = new THREE.CylinderGeometry(0.012, 0.012, len, 3);
        g.applyQuaternion(new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), d.normalize()));
        g.translate((a.x + b.x) / 2, (a.y + b.y) / 2, (a.z + b.z) / 2);
        kt.add(g, CORD, new THREE.Matrix4());
        g.dispose();
      };
      for (let i = 0; i < COLS; i++) for (let j = 0; j < ROWS - 1; j++) seg(pt(i, j), pt(i, j + 1));
      for (let j = 0; j < ROWS; j++) for (let i = 0; i < COLS - 1; i++) seg(pt(i, j), pt(i + 1, j));
      for (let i = 0; i < COLS; i += 2) kt.add(ball, CORK, new THREE.Matrix4().makeTranslation(pt(i, 0).x, pt(i, 0).y, pt(i, 0).z), place(0, 0, 0, 0, new THREE.Vector3(0.07, 0.055, 0.07)));
    };
    {
      const kt = new Kit();
      // Snagged across the reef's rocks, sagging down to the sand between them.
      drape(kt, 0.85, 0.55, 0.12, place(reef.x, rg + 0.75, reef.z, Math.atan2(sideX, sideZ) + Math.PI / 2, 1, -0.9));
      drape(kt, 0.6, 0.4, 0.1, place(reef.x - outX * 0.3, rg + 0.55, reef.z - outZ * 0.3, Math.atan2(sideX, sideZ) + 1.9, 1, -1.1));
      this.reefNet = mesh(kt, 'shore-net-reef');
    }
    {
      const kt = new Kit();
      const gy = ctx.ground(hang.x, hang.z);
      drape(kt, 0.95, 1.0, 0.18, place(hang.x, gy + 1.15, hang.z, Math.atan2(-hang.x, -hang.z) + Math.PI / 2));
      this.homeNet = mesh(kt, 'shore-net-home');
      this.homeNet.visible = false;
    }
    {
      const kt = new Kit();
      // A rolled bundle of wet net: a squashed heap with cords wound round, corks poking out.
      kt.add(ball, '#4e5646', place(0, 0.18, 0, 0, new THREE.Vector3(0.8, 0.38, 0.55)));
      kt.add(ball, '#5a6252', place(0.12, 0.26, 0.05, 0.5, new THREE.Vector3(0.55, 0.3, 0.4)));
      for (let k = 0; k < 3; k++) kt.add(new THREE.TorusGeometry(0.3, 0.02, 4, 12), CORD, place(-0.25 + k * 0.25, 0.18, 0, 0, new THREE.Vector3(1, 0.7, 0.95), 0, Math.PI / 2));
      for (let k = 0; k < 5; k++) kt.add(ball, CORK, place(R() * 0.35, 0.3 + 0.06 * rng(), R() * 0.22, 0, 0.07));
      kt.add(box, CORD, place(0.42, 0.06, 0.1, 0.4, new THREE.Vector3(0.3, 0.02, 0.02)));
      this.bundle = mesh(kt, 'shore-net-bundle');
      this.bundle.visible = false;
      this.bundle.geometry.computeBoundingSphere();
    }

    // ---- The ropes from a Drowned to the hero it drags.
    const ropeG = new THREE.CylinderGeometry(0.045, 0.045, 1, 5).translate(0, 0.5, 0);
    const ropeM = new THREE.MeshStandardMaterial({ color: '#9a8566', roughness: 0.95 });
    this.geos.push(ropeG);
    this.mats.push(ropeM);
    this.ropes = new THREE.InstancedMesh(ropeG, ropeM, MAX_ROPES);
    this.ropes.count = 0;
    this.ropes.frustumCulled = false;
    this.ropes.name = 'shore-ropes';
    this.group.add(this.ropes);

    // ---- The sea: one wide sheet, as high as the highest ground it will cover.
    let top = -Infinity;
    for (let i = 0; i < 64; i++) {
      const x = R() * span;
      const z = R() * span;
      if (out(x / K, z / K) < r.shore) continue;
      top = Math.max(top, ctx.ground(x, z));
    }
    top = Math.max(top, rg, ctx.ground(0, 0));
    const big = (map.radius + 16) * WORLD_PER_HEX;
    const seaG = new THREE.PlaneGeometry(big * 2, big * 2, 72, 72).rotateX(-Math.PI / 2);
    this.geos.push(seaG);
    this.seaM = seaMat(this.dir);
    this.mats.push(this.seaM);
    this.sea = new THREE.Mesh(seaG, this.seaM);
    this.sea.position.y = top + 0.14;
    this.sea.name = 'shore-sea';
    this.sea.renderOrder = 3;
    this.sea.frustumCulled = false;
    this.group.add(this.sea);

    this.group.add(this.glow.mesh);
    for (const g of [box, cyl, rock, disc, cone, ball]) g.dispose();
    this.group.updateMatrixWorld(true);

    this.sky = captureSky(ctx);
  }

  /** Repaints the bare sea floor on the terrain: wet sand past the shore line, darker the further out. */
  apply(terrain: TerrainBuild): void {
    const tint = (mesh: THREE.Mesh): void => {
      const geo = mesh.geometry;
      const col = geo.getAttribute('color') as THREE.BufferAttribute | undefined;
      const pos = geo.getAttribute('position') as THREE.BufferAttribute | undefined;
      if (!col || !pos) return;
      mesh.updateMatrixWorld(true);
      const p = new THREE.Vector3();
      const c = new THREE.Color();
      for (let i = 0; i < pos.count; i++) {
        p.fromBufferAttribute(pos, i).applyMatrix4(mesh.matrixWorld);
        const o = (p.x / K) * (this.dir.x / 1000) + (p.z / K) * (this.dir.y / 1000);
        const t = (o - (this.r.shore - 700)) / 1400;
        if (t <= 0) continue;
        const f = Math.min(1, t);
        const deep = Math.min(1, Math.max(0, (o - this.r.shore) / 7000));
        c.setRGB(col.getX(i), col.getY(i), col.getZ(i));
        const l = 0.85 + 0.6 * (0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b);
        this.c.copy(SAND).lerp(WET, Math.min(1, f * 1.4)).lerp(WET_FAR, deep).multiplyScalar(Math.min(1.25, l));
        c.lerp(this.c, f);
        col.setXYZ(i, c.r, c.g, c.b);
      }
      col.needsUpdate = true;
    };
    tint(terrain.mesh);
    // Nothing grows on the sea floor: the trees, bushes and grass out there go (the rocks stay).
    const zero = new THREE.Matrix4().makeScale(0, 0, 0);
    const m = new THREE.Matrix4();
    const p = new THREE.Vector3();
    terrain.decor.traverse((o) => {
      if (o.name === 'outskirts-ground' || o.name === 'roads') tint(o as THREE.Mesh);
      const im = o as THREE.InstancedMesh;
      if (!im.isInstancedMesh || o.name === 'rocks') return;
      im.updateMatrixWorld(true);
      let hid = false;
      for (let i = 0; i < im.count; i++) {
        im.getMatrixAt(i, m);
        p.setFromMatrixPosition(m).applyMatrix4(im.matrixWorld);
        const out = (p.x / K) * (this.dir.x / 1000) + (p.z / K) * (this.dir.y / 1000);
        if (out < this.r.shore - 300) continue;
        im.setMatrixAt(i, zero);
        hid = true;
      }
      if (hid) im.instanceMatrix.needsUpdate = true;
    });
  }

  /** Every fresh tick: the water's events, spray at its front, heroes wading, the net's moments. */
  tick(s: BattleState): void {
    const low = this.ctx.low;
    const st = s.tide;
    for (const e of s._events ?? []) {
      if (e.t !== 'tide') continue;
      const p = this.ctx.at(e.x, e.y, 0.1);
      if (e.kind === 'back') {
        // Far out, the sea turns: a roar and a white burst over the reef.
        this.ctx.flare(this.reef.clone().setY(this.reef.y + 1.2), '#ffffff', 3.5, 20);
        this.ctx.vfx.burst(this.reef.clone().setY(this.reef.y + 0.5), { n: low ? 10 : 26, color: '#ffffff', speed: 0.09, up: 0.1, size: 0.12, life: 26, gravity: 0.01, spread: 1.6 });
        this.ctx.vfx.burst(this.reef.clone().setY(this.reef.y + 0.6), { n: low ? 2 : 4, color: '#e6f2ff', speed: 0.02, up: 0.03, size: 1.4, life: 30, smoke: true, spread: 1.2 });
        this.ctx.shake(0.35);
      } else if (e.kind === 'loose') {
        this.ctx.vfx.ring(p, 0.3, 1.8, '#cfe8ff', 12, 0.8);
        this.ctx.flare(p.clone().setY(p.y + 0.6), '#e8f4ff', 1.8, 10);
      } else if (e.kind === 'lift') {
        this.ctx.vfx.burst(p.clone().setY(p.y + 0.4), { n: low ? 4 : 10, color: '#bfe0ff', speed: 0.04, up: 0.05, size: 0.05, life: 14, gravity: 0.01, spread: 0.4 });
      } else if (e.kind === 'home') {
        this.ctx.flare(this.ctx.at(0, 0, 1.6), '#ffd6a0', 3, 16);
        this.ctx.vfx.ring(this.ctx.at(0, 0, 0.1), 0.4, 2.6, '#ffd6a0', 14, 0.7);
      } else if (e.kind === 'dawn') {
        this.ctx.flare(this.ctx.at(0, 0, 2.4), '#ffb08a', 5, 30);
      } else if (e.kind === 'lost') {
        this.ctx.vfx.burst(p.clone().setY(p.y + 0.3), { n: low ? 8 : 20, color: '#ffffff', speed: 0.07, up: 0.08, size: 0.09, life: 18, gravity: 0.012, spread: 0.8 });
        this.ctx.vfx.ring(p, 0.2, 2.2, '#cfe8ff', 14, 0.8);
      } else if (e.kind === 'grab') {
        this.ctx.vfx.burst(p.clone().setY(p.y + 0.4), { n: low ? 5 : 12, color: '#9fc4d0', speed: 0.05, up: 0.05, size: 0.06, life: 14, gravity: 0.012, spread: 0.4 });
        this.ctx.vfx.ring(p, 0.2, 1.2, '#5a8a9a', 10, 0.8);
      } else if (e.kind === 'free') {
        this.ctx.vfx.burst(p.clone().setY(p.y + 0.6), { n: low ? 5 : 12, color: '#e8f4ff', speed: 0.06, up: 0.05, size: 0.05, life: 12, gravity: 0.01, spread: 0.5 });
      }
    }
    if (!st?.back) return;
    // Spray thrown up along the front, across the way.
    const f = st.front;
    const fx = (this.dir.x * f) / 1000;
    const fy = (this.dir.y * f) / 1000;
    const nx = -this.dir.y / 1000;
    const ny = this.dir.x / 1000;
    const n = low ? 4 : 9;
    for (let k = 0; k < n; k++) {
      const off = ((k / (n - 1)) * 2 - 1) * 9000 + (s.tick % 7) * 120;
      const p = this.ctx.at(fx + nx * off, fy + ny * off, 0.2);
      p.y = this.sea.position.y + 0.05;
      this.ctx.vfx.burst(p, { n: 1, color: k % 2 ? '#ffffff' : '#d8ecff', speed: 0.03, up: 0.07, size: 0.1, life: 12, gravity: 0.012, spread: 0.4 });
    }
    // Heroes wading in it splash.
    for (const h of s.heroes) {
      if (h.downTicks > 0 || (s.tick + h.id) % (low ? 6 : 3) !== 0) continue;
      if ((h.x * this.dir.x + h.y * this.dir.y) / 1000 < f) continue;
      const hp = this.ctx.pos(h.id) ?? this.ctx.at(h.x, h.y, 0);
      this.v.copy(hp).setY(this.sea.position.y + 0.05);
      this.ctx.vfx.burst(this.v, { n: low ? 2 : 4, color: '#dff0ff', speed: 0.03, up: 0.05, size: 0.06, life: 10, gravity: 0.012, spread: 0.35 });
      this.ctx.vfx.ring(this.v, 0.15, 0.6, '#cfe8ff', 8, 0.6);
    }
  }

  /** Every redraw: the sea up to its front, the net where it is, the ropes, the lamps, and the light of the hour. */
  draw(s: BattleState): void {
    const st = s.tide;
    const r = this.r;
    this.seaM.uniforms.front!.value = st?.front ?? r.start;
    this.seaM.uniforms.time!.value = this.now;
    // The hour: day while the sea is out; a moonlit night once it turns; a warm dawn at the end.
    const night = st?.back ? Math.min(1, (s.tick - st.back) / 180) : 0;
    const dawn = st?.done ? Math.min(1, (s.tick - st.done) / 30) : s.waveT >= r.dawn ? 1 : 0;
    this.light(night, dawn);
    this.seaM.uniforms.night!.value = night * (1 - dawn * 0.7);

    const net = st?.net;
    const at = net?.at ?? 'reef';
    this.reefNet.visible = at === 'reef';
    this.homeNet.visible = at === 'home';
    this.bundle.visible = at === 'ground' || at === 'carried';
    const pulse = 0.5 + 0.5 * Math.sin(this.now * 4);
    this.glow.begin();
    if (net && (at === 'ground' || at === 'carried')) {
      if (at === 'carried') {
        const h = s.heroes.find((x) => x.pid === net.pid);
        const hp = h ? this.ctx.pos(h.id) : undefined;
        this.v.copy(hp ?? this.ctx.at(net.x, net.y, 0));
        this.v.y += 1.15;
        this.bundle.position.copy(this.v);
        this.bundle.rotation.set(0.2, h ? (h.facing / 1024) * -Math.PI * 2 : 0, 0);
        this.bundle.scale.setScalar(0.62);
      } else {
        this.v.copy(this.ctx.at(net.x, net.y, 0));
        this.v.y = this.ctx.ground(this.v.x, this.v.z);
        this.bundle.position.copy(this.v);
        this.bundle.rotation.set(0, 0.7, 0);
        this.bundle.scale.setScalar(1.1);
        this.glow.add(this.v.x, this.v.y + 0.05, this.v.z, COOL, 2.2, 0.22 + 0.2 * pulse);
        this.glow.add(this.v.x, this.v.y + 0.45, this.v.z, COOL, 0.7, 0.35 + 0.25 * pulse);
      }
    }
    // Snagged: a ring of cold dots round it fills as it is worked loose.
    if (at === 'reef') {
      const k = Math.min(1, (net?.held ?? 0) / Math.max(1, r.unhook));
      const N = 16;
      const y = this.ctx.ground(this.reef.x, this.reef.z) + 0.1;
      for (let i = 0; i < N; i++) {
        const a = (i / N) * Math.PI * 2;
        const on = i < Math.round(k * N);
        this.glow.add(this.reef.x + Math.cos(a) * 1.25, y, this.reef.z + Math.sin(a) * 1.25, on ? FOAM : COOL, on ? 0.16 : 0.1, on ? 0.95 : 0.3 + 0.2 * pulse);
      }
      this.glow.add(this.reef.x, y + 0.7, this.reef.z, COOL, 0.6, 0.12 + 0.1 * pulse);
    }
    // The huts' windows and the walk's lamp, brighter as the night comes on.
    for (const l of this.lamps) this.glow.add(l.x, l.y, l.z, LAMP, 0.6 + 0.5 * night, 0.4 + 0.45 * night * (1 - dawn));
    this.glow.end();

    // The ropes: a Drowned to the hero in its grip.
    let n = 0;
    for (const e of s.enemies) {
      if (!e.grab || e.hp <= 0 || n >= MAX_ROPES) continue;
      const h = s.heroes.find((x) => x.id === e.grab);
      const ep = this.ctx.pos(e.id);
      const hp = h ? this.ctx.pos(h.id) : undefined;
      if (!ep || !hp) continue;
      this.v.copy(ep).setY(ep.y + 0.7);
      this.v2.copy(hp).setY(hp.y + 0.55);
      const d = this.v2.clone().sub(this.v);
      const len = d.length();
      if (len < 0.01) continue;
      this.q.setFromUnitVectors(UPV, d.normalize());
      this.ropes.setMatrixAt(n++, this.m.compose(this.v, this.q, this.s3.set(1, len, 1)));
    }
    this.ropes.count = n;
    this.ropes.instanceMatrix.needsUpdate = true;
  }

  /** The light of the hour: day, the moonlit night once the water has turned, and the dawn. */
  private light(night: number, dawn: number): void {
    paintHour(this.ctx, this.sky, night, dawn);
  }

  frame(now: number): void {
    this.now = now;
  }

  dispose(): void {
    for (const g of this.geos) g.dispose();
    for (const m of this.mats) m.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
  }
}


/**
 * The fishers' stilt-houses on the node, facing the sea (`outX`, `outZ`: the way out, in world): three huts on poles round
 * a boardwalk, boats pulled up with their oars, nets drying, a lamp on a pole and crates at the walk's head. The lamps'
 * places go into `lamps`; returns where the great net's (empty) frame stands.
 */
export const stiltHouses = (
  ctx: OmenCtx,
  kit: Kit,
  lit: Kit,
  outX: number,
  outZ: number,
  R: () => number,
  lamps: THREE.Vector3[],
  geo: { box: THREE.BufferGeometry; cyl: THREE.BufferGeometry; ball: THREE.BufferGeometry },
): THREE.Vector3 => {
  const { box, cyl, ball } = geo;
  // ---- The stilt-houses on the node: three huts on poles round a boardwalk, facing the sea.
  const g0 = ctx.ground(0, 0);
  const deck = g0 + 0.55;
  const yawSea = Math.atan2(outX, outZ);
  // The boardwalk: a plank deck on poles round the middle, open where the heroes stand.
  for (let i = 0; i < 10; i++) {
    const a = (i / 10) * Math.PI * 2;
    const x = Math.cos(a) * 1.05;
    const z = Math.sin(a) * 1.05;
    kit.add(box, PLANK, place(x, deck, z, -a + Math.PI / 2, new THREE.Vector3(0.72, 0.05, 0.34)));
    kit.add(cyl, TIMBER_D, place(x, (g0 + deck) / 2, z, 0, new THREE.Vector3(0.07, deck - g0 + 0.05, 0.07)));
  }
  const huts: [number, number][] = [
    [1.75, 0.1],
    [1.85, 2.2],
    [1.7, 4.2],
  ];
  for (const [d, a0] of huts) {
    const a = yawSea + a0 + Math.PI;
    const x = Math.sin(a) * d;
    const z = Math.cos(a) * d;
    const gy = ctx.ground(x, z);
    const floor = gy + 0.75;
    const yaw = Math.atan2(-x, -z);
    // Poles, the floor, plank walls, a pitched thatch roof, a door, a ladder down.
    for (const [px, pz] of [[-0.42, -0.36], [0.42, -0.36], [-0.42, 0.36], [0.42, 0.36]] as const) {
      const p = new THREE.Vector3(px, 0, pz).applyAxisAngle(new THREE.Vector3(0, 1, 0), yaw);
      kit.add(cyl, TIMBER_D, place(x + p.x, (gy + floor) / 2 - 0.05, z + p.z, 0, new THREE.Vector3(0.08, floor - gy + 0.1, 0.08)));
    }
    const hut = place(x, floor, z, yaw);
    kit.add(box, PLANK, hut, place(0, 0, 0, 0, new THREE.Vector3(1.05, 0.06, 0.95)));
    kit.add(box, TIMBER, hut, place(0, 0.32, 0, 0, new THREE.Vector3(0.82, 0.6, 0.72)));
    for (let k = -2; k <= 2; k++) kit.add(box, TIMBER_D, hut, place(k * 0.17, 0.32, 0.365, 0, new THREE.Vector3(0.012, 0.6, 0.01)));
    for (const s of [-1, 1]) kit.add(box, THATCH, hut, place(s * 0.26, 0.76, 0, 0, new THREE.Vector3(0.6, 0.06, 0.95), 0, s * -0.62));
    kit.add(box, '#4a3c2c', hut, place(0, 0.98, 0, 0, new THREE.Vector3(0.08, 0.06, 0.98)));
    kit.add(box, '#2a2018', hut, place(0, 0.26, 0.37, 0, new THREE.Vector3(0.22, 0.42, 0.02)));
    // A lit window by the door.
    lit.add(box, '#ffc890', hut, place(0.25, 0.4, 0.37, 0, new THREE.Vector3(0.12, 0.1, 0.02)));
    lamps.push(new THREE.Vector3(0.25, 0.4, 0.39).applyMatrix4(hut));
    // The ladder down to the sand.
    for (const s of [-1, 1]) kit.add(box, TIMBER_D, hut, place(s * 0.1, -0.38, 0.6, 0, new THREE.Vector3(0.03, 0.85, 0.03), 0.35));
    for (let k = 0; k < 4; k++) kit.add(box, TIMBER, hut, place(0, -0.7 + k * 0.2, 0.47 + 0.07 * (3 - k), 0, new THREE.Vector3(0.22, 0.025, 0.04)));
  }
  // Boats pulled up on the sand, one keel up and one on its keel, with their oars.
  // An open hull: a lathe out from the keel to the rim and back in, so both its outside and its inside show.
  const hull = new THREE.LatheGeometry(
    [[0.0, -0.5], [0.3, -0.45], [0.47, -0.24], [0.5, 0], [0.45, 0], [0.42, -0.22], [0.27, -0.38], [0.0, -0.42]].map(([r, y]) => new THREE.Vector2(r as number, y as number)),
    14,
  );
  const gunwale = new THREE.TorusGeometry(0.5, 0.04, 4, 16).rotateX(Math.PI / 2);
  for (const [d, a0, flip] of [[2.7, 1.15, false], [2.6, -1.0, true]] as const) {
    const a = yawSea + a0 + Math.PI;
    const x = Math.sin(a) * d;
    const z = Math.cos(a) * d;
    const gy = ctx.ground(x, z);
    const boat = place(x, gy + (flip ? 0.02 : 0.2), z, a + 0.4, 1, flip ? Math.PI : 0);
    const shape = new THREE.Vector3(1.6, 0.42, 0.62);
    kit.add(hull, '#4e5a5a', boat, place(0, 0, 0, 0, shape));
    kit.add(gunwale, '#6c665a', boat, place(0, 0, 0, 0, shape.clone().setY(1)));
    // A stripe of old red paint under the rim, the thwarts and a keel.
    kit.add(new THREE.TorusGeometry(0.47, 0.025, 4, 16).rotateX(Math.PI / 2), '#8a3a30', boat, place(0, -0.06, 0, 0, shape.clone().setY(1)));
    if (!flip) for (const k of [-0.25, 0.2]) kit.add(box, PLANK, boat, place(k * 1.6, -0.06, 0, 0, new THREE.Vector3(0.1, 0.03, 0.56)));
    kit.add(box, TIMBER_D, boat, place(0, -0.2, 0, 0, new THREE.Vector3(1.5, 0.05, 0.04)));
    // Its oars on the sand beside it.
    for (const k of [-1, 1]) kit.add(box, '#7a6e58', place(x + Math.cos(a) * 0.55 * k + 0.1 * k, gy + 0.03, z - Math.sin(a) * 0.55 * k, a + 0.3 * k, new THREE.Vector3(1.3, 0.025, 0.05)));
  }
  hull.dispose();
  gunwale.dispose();
  // Nets drying on a frame by the huts; the frame where the great net will hang, empty for now.
  const frameAt = (d: number, a0: number): THREE.Vector3 => {
    const a = yawSea + a0 + Math.PI;
    return new THREE.Vector3(Math.sin(a) * d, 0, Math.cos(a) * d);
  };
  const dry = frameAt(2.5, 3.2);
  const hang = frameAt(2.6, -2.25);
  for (const [p, w] of [[dry, 0.7], [hang, 1.0]] as const) {
    const gy = ctx.ground(p.x, p.z);
    const yaw = Math.atan2(-p.x, -p.z) + Math.PI / 2;
    const f = place(p.x, gy, p.z, yaw);
    for (const s of [-1, 1]) kit.add(cyl, '#8a7a66', f, place(s * w, 0.6, 0, 0, new THREE.Vector3(0.06, 1.2, 0.06)));
    kit.add(cyl, '#8a7a66', f, place(0, 1.18, 0, 0, new THREE.Vector3(0.05, w * 2 + 0.2, 0.05), 0, Math.PI / 2));
    if (p === dry) for (let k = 0; k < 7; k++) kit.add(box, CORD, f, place(-0.6 + k * 0.2, 0.78, 0.02, 0, new THREE.Vector3(0.012, 0.78, 0.012), 0, 0.08 * R()));
    if (p === dry) for (let k = 0; k < 4; k++) kit.add(box, CORD, f, place(0, 0.5 + k * 0.18, 0.03, 0, new THREE.Vector3(1.3, 0.012, 0.012)));
  }
  // Crates, barrels and a lamp on a pole at the walk's head, toward the sea.
  {
    const p = frameAt(1.0, Math.PI);
    const gy = ctx.ground(p.x, p.z);
    kit.add(cyl, TIMBER_D, place(p.x, gy + 0.8, p.z, 0, new THREE.Vector3(0.06, 1.6, 0.06)));
    lit.add(ball, '#ffd6a0', place(p.x, gy + 1.62, p.z, 0, 0.14));
    lamps.push(new THREE.Vector3(p.x, gy + 1.62, p.z));
    for (let k = 0; k < 3; k++) kit.add(cyl, k % 2 ? '#6a4e34' : '#5a4430', place(p.x + 0.35 + k * 0.28, gy + 0.18, p.z + 0.2, 0, new THREE.Vector3(0.24, 0.36, 0.24)));
  }

  return hang;
};

/** The scene's light as it was before the hour changed it (to lerp from). */
export type Sky = { hemi: [THREE.Color, number] | null; key: [THREE.Color, number] | null; rim: [THREE.Color, number] | null; bg: THREE.Color | null; fog: THREE.Color | null };

export const captureSky = (ctx: OmenCtx): Sky => {
  const bg = ctx.scene.background instanceof THREE.Color ? ctx.scene.background.clone() : null;
  const fog = ctx.scene.fog instanceof THREE.Fog ? ctx.scene.fog.color.clone() : null;
  return { hemi: ctx.hemi ? [ctx.hemi.color.clone(), ctx.hemi.intensity] : null, key: ctx.key ? [ctx.key.color.clone(), ctx.key.intensity] : null, rim: ctx.rim ? [ctx.rim.color.clone(), ctx.rim.intensity] : null, bg, fog };
};

/** The light of the hour on the coast: `night` 0..1 to a moonlit night, `dawn` 0..1 to a warm sunrise over it. */
export const paintHour = (ctx: OmenCtx, sky: Sky, night: number, dawn: number): void => {
  const sk = sky;
  const moon = new THREE.Color('#6a7ab0');
  const sun = new THREE.Color('#ffb08a');
  const dim = 1 - 0.62 * night + 0.5 * night * dawn;
  if (sk.hemi && ctx.hemi) {
    ctx.hemi.color.copy(sk.hemi[0]).lerp(moon, night * 0.7).lerp(sun, dawn * 0.45);
    ctx.hemi.intensity = sk.hemi[1] * dim;
  }
  if (sk.key && ctx.key) {
    ctx.key.color.copy(sk.key[0]).lerp(new THREE.Color('#9fb4e0'), night * 0.8).lerp(sun, dawn * 0.7);
    ctx.key.intensity = sk.key[1] * (1 - 0.62 * night + 0.55 * night * dawn);
  }
  if (sk.rim && ctx.rim) {
    ctx.rim.color.copy(sk.rim[0]).lerp(sun, dawn * 0.6);
    ctx.rim.intensity = sk.rim[1] * (1 - 0.3 * night + 0.5 * dawn);
  }
  const bg = ctx.scene.background;
  if (sk.bg && bg instanceof THREE.Color) bg.copy(sk.bg).lerp(new THREE.Color('#0e1626'), night * 0.85).lerp(new THREE.Color('#4a3040'), dawn * 0.6);
  const fog = ctx.scene.fog;
  if (sk.fog && fog instanceof THREE.Fog) fog.color.copy(sk.fog).lerp(new THREE.Color('#141c2c'), night * 0.8).lerp(new THREE.Color('#5a3a44'), dawn * 0.5);
};

const UPV = new THREE.Vector3(0, 1, 0);

/** A hex cell's centre in world (x, z), as the sim's hexToWorld then the board's scale. */
const cellWorld = (q: number, r: number): { x: number; z: number } => ({ x: (q * 1000 + (r * 1000) / 2) * K, z: r * 866 * K });

/** A small seeded random (the layout is the same every time for the same reef). */
const mulberry = (seed: number): (() => number) => {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};
